import React, { useState, useEffect } from "react";
import { Link, useParams} from "react-router-dom";
import { useManualQuery } from "graphql-hooks";
import {
  Box,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  TablePagination,
  TableSortLabel,
  Tooltip,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import {
  ArrowDropUp,
  ArrowDropDown,
  ArrowUpward,
  ArrowDownward,
} from "@mui/icons-material";
import PublicPageLayout from "./PublicPageLayout";
import FloatingMenu from "../components/FloatingMenu";
import EmptyState from "../components/EmptyState";
import { isValidUUIDv4 } from "../hooks/uuid";
import { TOY_LIST_BY_USER_UUID } from "../graphQL/toy_qry";
import AlertPopup from "../components/AlertPopup";
import placeholderImage from "../assets/r3play-logo-sml.png";

import useIsTablet from "../hooks/useIsTablet";
import useIsMobile from "../hooks/useIsMobile";

const PublicListings = () => {
  const { userUuid } = useParams();

  const isTablet = useIsTablet(1360);
  const isMobile = useIsMobile(833);
  const [mobileSortBy, setMobileSortBy] = useState("publishedDate");
  const [sortPanelOpen, setSortPanelOpen] = useState(false);
  const [orderBy, setOrderBy] = useState("publishedDate");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(0);
  const [listings, setListings] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 5 : 10);
  const [floatMenuLeftMargin, setFloatMenuLeftMargin] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [warningPopup, setWarningPopup] = useState({
    open: false,    
  });

  const [getToyList] = useManualQuery(TOY_LIST_BY_USER_UUID);

  const formatter = new Intl.DateTimeFormat("en-us", {
    year: "numeric",
    month: "long",
    day: "2-digit",
  });

  const dayFormat = day => {
    const dayDate = new Date(day);
    const dayFormat = formatter.formatToParts(dayDate);
    const newFormat =
      dayFormat.find(a => a.type == "month").value +
      " " +
      dayFormat.find(a => a.type == "day").value +
      ", " +
      dayFormat.find(a => a.type == "year").value;
    return newFormat;
  };

  useEffect(() => {
    if (isMobile && rowsPerPage === 10) setRowsPerPage(5);
    else if (!isMobile && rowsPerPage === 5) setRowsPerPage(10);
  }, [rowsPerPage, isMobile, isTablet]);

  // Set floating menu place based on screen size
  useEffect(() => {
    const handleResize = () => {      
      setFloatMenuLeftMargin(window.innerWidth - 100);
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);
  }, []);

  // Handle click outside to close sort panel in Mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      const sortPanel = document.querySelector("[data-sort-panel]");
      if (sortPanel && !sortPanel.contains(event.target) && sortPanelOpen) {
        setSortPanelOpen(false);
      }
    };

    if (sortPanelOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [sortPanelOpen]);

  const setToyList = async () => {
    if (!userUuid) return;
    if (isValidUUIDv4(userUuid)){  
        const {
          loading: loadingToyList,
          error: errorToyList,
          data: toyList,
        } = await getToyList({
          variables: {
            userUuid: userUuid,
          },
        });
        if (!loadingToyList && toyList) {      
          setListings(
              toyList.toyListByUseruuid.map(toy => ({
                id: toy.toyId,
                photo: toy.toyImages[0]?.fileName || placeholderImage,
                title: toy.title,
                category: toy.categoryId.title,
                subCategory: toy.subCategoryId.title,
                originalPrice: toy.originalPrice,
                sellingPrice: toy.sellingPrice,            
                publishedDate: dayFormat(toy.publishedDate),
              }))
            );
          setIsLoaded(true);
            
        } else {
          console.log("error: ", errorToyList);
        }
    } else {
      setWarningPopup({
        open: true,
        title: "This URL is not valid.",
      });
    }
  };

  
  useEffect(() => {
    (async () => setToyList())();
  }, [userUuid]);

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
  };

  // Sorting handler
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setMobileSortBy(property);
  };

  // Sort data
  const getSortedData = () => {
    let sortedData = [...listings];

    if (orderBy) {
      sortedData.sort((a, b) => {
        let aValue = a[orderBy];
        let bValue = b[orderBy];

        if (orderBy === "title") {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (orderBy === "category") {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (orderBy === "originalPrice") {
          aValue = parseFloat(aValue);
          bValue = parseFloat(bValue);
        }

        if (orderBy === "sellingPrice") {
          aValue = parseFloat(aValue);
          bValue = parseFloat(bValue);
        }

        if (orderBy === "publishedDate") {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        }        

        if (aValue < bValue) {
          return order === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return order === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return sortedData;
  };

  const sortedData = getSortedData();
  const paginatedData = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (    
    <PublicPageLayout 
      activeMenuItem="listings"
      userUuid={userUuid}>
      {/* Mobile View - Only shows on screens < 834px */}
      <Box
        sx={{
          display: "none",
          "@media (max-width: 833px)": {
            display: "block",
          },
        }}
      >
        {/* Mobile Header */}
        <Box p={2}>
          <Typography variant="h6">Listings</Typography>
          <Typography variant="body2" color="text.secondary">
            Explore all toys listed by this user for sale or exchange.
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {!isLoaded ? (
            <Box p={4} textAlign="center">Loading...</Box>
              ) 
              : isLoaded && listings.length === 0 ? 
              (
                <EmptyState 
                  type="listings"
                  title="No Listings Available"
                  description="No listings are available right now. Please check back later."
                />
              ) : (
              <>       

            {/* Mobile Sort Panel */}
            <Box pr={2} pl={2}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                  bgcolor: "#F5F5F5", // Gray box around
                  p: "16px",
                  borderRadius: 2,
                  "@media (max-width: 833px)": {
                    flexDirection: "column",
                    alignItems: "stretch",
                  },
                }}
              >
                <Box sx={{ position: "relative", width: "100%" }} data-sort-panel>
                  <TextField
                    label="Sort by"
                    value={
                      mobileSortBy
                        ? (() => {
                            const options = {
                              title: "Title",
                              category: "Category",
                              originalPrice: "Original Price", 
                              sellingPrice: "Selling Price",                            
                              publishedDate: "Published Date",
                            };
                            return options[mobileSortBy] || "";
                          })()
                        : ""
                    }
                    onClick={() => setSortPanelOpen(!sortPanelOpen)}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                        >
                          {mobileSortBy &&
                            (order === "desc" ? (
                              <ArrowDownward
                                sx={{ fontSize: 20, color: "#666" }}
                              />
                            ) : (
                              <ArrowUpward sx={{ fontSize: 20, color: "#666" }} />
                            ))}
                          {sortPanelOpen ? (
                            <ArrowDropUp sx={{ color: "#666" }} />
                          ) : (
                            <ArrowDropDown sx={{ color: "#666" }} />
                          )}
                        </Box>
                      ),
                    }}
                    sx={{
                      width: "100%",
                      cursor: "pointer",
                      "& .MuiOutlinedInput-root": {
                        height: "40px",
                        backgroundColor: "white",
                        cursor: "pointer",
                        paddingRight: "8px",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#d0d0d0",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#e0e0e0",
                          borderWidth: "1px",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "#666",
                        fontSize: "14px",
                        "&.Mui-focused": {
                          color: "#666",
                        },
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e0e0e0",
                        pr: 0,
                      },
                      "& .MuiOutlinedInput-input": {
                        cursor: "pointer",
                      },
                    }}
                  />

                  {/* Sort options panel */}
                  {sortPanelOpen && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: "40px",
                        left: 0,
                        right: 0,
                        backgroundColor: "white",
                        border: "1px solid #e0e0e0",
                        borderTop: "none",
                        borderRadius: "0 0 4px 4px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        zIndex: 1000,
                      }}
                    >
                      <RadioGroup
                        value={mobileSortBy}
                        onChange={(e) => {
                          setMobileSortBy(e.target.value);
                          setOrder("desc");
                          setOrderBy(e.target.value);
                          setPage(0);
                        }}
                      >
                        {[
                           { value: "title", label: "Title" },
                           { value: "category", label: "Category" },
                           { value: "originalPrice", label: "Orignal Price" },
                           { value: "sellingPrice", label: "Selling Price" },
                           { value: "publishedDate", label: "Published Date" },
                        ].map((option) => (
                          <Box
                            key={option.value}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              px: 1,
                              pl: 0,
                              py: 0.5,
                              "&:hover": {
                                backgroundColor: "#f5f5f5",
                              },
                            }}
                          >
                            <FormControlLabel
                              value={option.value}
                              control={
                                <Radio
                                  size="small"
                                  sx={{
                                    color: "#666",
                                  }}
                                />
                              }
                              label={
                                <Typography
                                  sx={{ fontSize: "14px", color: "#333" }}
                                >
                                  {option.label}
                                </Typography>
                              }
                              sx={{ margin: 0, flex: 1 }}
                            />
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                width: "55px",
                                justifyContent: "space-between",
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMobileSortBy(option.value);
                                  setOrder("desc");
                                  setOrderBy(option.value);
                                  setPage(0);
                                  setSortPanelOpen(false);
                                }}
                                sx={{
                                  p: 0.25,
                                  backgroundColor:
                                    mobileSortBy === option.value &&
                                    order === "desc"
                                      ? "#fce4ec"
                                      : "transparent",
                                }}
                              >
                                <ArrowDownward
                                  fontSize="small"
                                  sx={{ fontSize: 20, color: "#666" }}
                                />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMobileSortBy(option.value);
                                  setOrder("asc");
                                  setOrderBy(option.value);
                                  setPage(0);
                                  setSortPanelOpen(false);
                                }}
                                sx={{
                                  p: 0.25,
                                  backgroundColor:
                                    mobileSortBy === option.value &&
                                    order === "asc"
                                      ? "#fce4ec"
                                      : "transparent",
                                }}
                              >
                                <ArrowUpward
                                  fontSize="small"
                                  sx={{ fontSize: 20, color: "#666" }}
                                />
                              </IconButton>
                            </Box>
                          </Box>
                        ))}
                      </RadioGroup>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {/* Mobile Cards */}
            <Box px={2}>
              {paginatedData.map((listing, index) => (
                <Box
                  key={listing.id}
                  sx={{
                    backgroundColor: index % 2 === 0 ? "#FDECF0" : "white",
                    borderRadius: "8px",
                    mb: 2,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                    overflow: "hidden",
                  }}
                >
                  <Table>
                    <TableBody>
                      {/* Row 1: Image + Title */}
                      <TableRow sx={{ borderBottom: "1px solid #e0e0e0" }}>
                        <TableCell sx={{ py: 2, width: "135px", border: "none" }}>
                          <Avatar
                            src={listing.photo}
                            alt={listing.title}
                            sx={{
                              width: 80,
                              height: 80,
                              borderRadius: "8px",
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 2, border: "none" }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#333",
                              fontWeight: "500",
                              lineHeight: 1.4,
                            }}
                          >
                            {listing.title}
                          </Typography>
                        </TableCell>
                      </TableRow>

                      {/* Row 2: Category + Subcategory */}
                      <TableRow>
                        <TableCell colSpan={2} sx={{ py: 2, border: "none" }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#666",
                              fontWeight: "bold",
                              mb: 0.5,
                            }}
                          >
                            Category > Subcategory
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#333",
                            }}
                          >
                            {listing.subCategory === "Don't Care"
                              ? listing.category
                              : `${listing.category} > ${listing.subCategory}`}
                          </Typography>                      
                        </TableCell>
                      </TableRow>

                      {/* Row 3: Original Price + Selling Price */}
                      <TableRow sx={{ borderBottom: "1px solid #e0e0e0" }}>
                        <TableCell sx={{ py: 2, border: "none" }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#666",
                              fontWeight: "bold",
                              mb: 0.5,
                            }}
                          >
                            Original Price
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#333",
                            }}
                          >
                            {listing.originalPrice
                              ? listing.originalPrice.toLocaleString(undefined, {
                                style: "currency",
                                currency: "USD",
                            })
                              : "----"
                            }
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 2, border: "none" }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#666",
                              fontWeight: "bold",
                              mb: 0.5,
                            }}
                          >
                            Selling Price
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#333",
                            }}
                          >
                            {listing.sellingPrice
                              ? listing.sellingPrice.toLocaleString(undefined, {
                                style: "currency",
                                currency: "USD",
                            })
                              : ""
                            }
                          </Typography>
                        </TableCell>
                      </TableRow>

                      {/* Row 4: Published Date */}
                      <TableRow>
                        <TableCell colSpan={2} sx={{ py: 2, border: "none" }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#666",
                              fontWeight: "bold",
                              mb: 0.5,
                            }}
                          >
                            Published Date
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#333",
                            }}
                          >
                            {listing.publishedDate}
                          </Typography>                      
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>

                  {/* Card bottom padding */}
                  <Box sx={{ pb: 2 }} />
                </Box>
              ))}
            </Box>

            {/* Mobile Pagination */}
            <Box
              sx={{
                backgroundColor: "white",
                borderRadius: "8px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <TablePagination
                rowsPerPageOptions={[5, 15, 20]}
                component="div"
                count={sortedData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  "& .MuiTablePagination-toolbar": {
                    paddingLeft: 1,
                    paddingRight: 1,
                    minHeight: "52px",
                  },
                  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                    fontSize: "14px",
                    color: "#666",
                  },
                  "& .MuiTablePagination-select": {
                    fontSize: "14px",
                  },
                  "& .MuiTablePagination-actions": {
                    "& .MuiIconButton-root": {
                      padding: "6px",
                    },
                  },
                }}
              />
          </Box>
        </>
        )}
      </Box>
      {/* Desktop/Tablet View - Only shows on screens >= 834px */}
      <Box
        sx={{
          "@media (max-width: 833px)": {
            display: "none",
          },
        }}
      >
        {/* Header Section */}

        <Box
          px={2}
          py={1}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* Title Section */}
          <Box>
            <Typography variant="h6">Listings</Typography>
            <Typography variant="body2" color="text.secondary">
              Explore all toys listed by this user for sale or exchange.
            </Typography>
          </Box>            
        </Box>

        <Divider />
        {!isLoaded ? (
            <Box p={4} textAlign="center">Loading...</Box>
              ) 
              : isLoaded && listings.length === 0 ? 
              (
                <EmptyState 
                  type="listings"
                  title="No Listings Available"
                  description="No listings are available right now. Please check back later."
                />
              ) : (
              <>   

          {/* Table */}
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: "600",
                      width: "120px",
                      color: "#333",
                      py: 2,
                    }}
                  >
                    Photo
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "600",
                      width: "300px",
                      color: "#333",
                      py: 2,
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === "title"}
                      direction={orderBy === "title" ? order : "asc"}
                      onClick={() => handleRequestSort("title")}
                      sx={{
                        fontWeight: "600",
                        color: "#333",
                        "&.Mui-active": {
                          color: "#333",
                        },
                        "&:hover": {
                          color: "#333",
                        },
                        "& .MuiTableSortLabel-icon": {
                          color: "#333 !important",
                        },
                      }}
                    >
                      Title/Summary
                    </TableSortLabel>
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: "600",
                      width: "300px",
                      color: "#333",
                      py: 2,
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === "category"}
                      direction={orderBy === "category" ? order : "asc"}
                      onClick={() => handleRequestSort("category")}
                      sx={{
                        fontWeight: "600",
                        color: "#333",
                        "&.Mui-active": {
                          color: "#333",
                        },
                        "&:hover": {
                          color: "#333",
                        },
                        "& .MuiTableSortLabel-icon": {
                          color: "#333 !important",
                        },
                      }}
                    >
                      Category > Subcategory
                    </TableSortLabel>
                  </TableCell>  

                  <TableCell
                    sx={{
                      fontWeight: "600",
                      width: "110px",
                      color: "#333",
                      py: 2,
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === "originalPrice"}
                      direction={orderBy === "originalPrice" ? order : "asc"}
                      onClick={() => handleRequestSort("originalPrice")}
                      sx={{
                        fontWeight: "600",
                        color: "#333",
                        "&.Mui-active": {
                          color: "#333",
                        },
                        "&:hover": {
                          color: "#333",
                        },
                        "& .MuiTableSortLabel-icon": {
                          color: "#333 !important",
                        },
                      }}
                    >
                      Original Price
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "600",
                      width: "170px",
                      color: "#333",
                      py: 2,
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === "sellingPrice"}
                      direction={orderBy === "sellingPrice" ? order : "asc"}
                      onClick={() => handleRequestSort("sellingPrice")}
                      sx={{
                        fontWeight: "600",
                        color: "#333",
                        "&.Mui-active": {
                          color: "#333",
                        },
                        "&:hover": {
                          color: "#333",
                        },
                        "& .MuiTableSortLabel-icon": {
                          color: "#333 !important",
                        },
                      }}
                    >
                      Selling Price
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "600",
                      width: "170px",
                      color: "#333",
                      py: 2,
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === "publishedDate"}
                      direction={orderBy === "publishedDate" ? order : "asc"}
                      onClick={() => handleRequestSort("publishedDate")}
                      sx={{
                        fontWeight: "600",
                        color: "#333",
                        "&.Mui-active": {
                          color: "#333",
                        },
                        "&:hover": {
                          color: "#333",
                        },
                        "& .MuiTableSortLabel-icon": {
                          color: "#333 !important",
                        },
                      }}
                    >
                      Published Date
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((listing) => (
                  <React.Fragment key={listing.id}>
                    <TableRow
                      sx={{
                        "&:hover": {
                          backgroundColor: "#f9f9f9",
                        },
                        ...(isTablet && {
                          "& td": {
                            borderBottom: "none",
                          },
                        }),
                      }}
                    >
                      <TableCell sx={{ py: 2, pr: 0, pb: isTablet ? 0 : 2 }}>
                        <Avatar
                          src={listing.photo}
                          alt={listing.title}
                          sx={{
                            width: isTablet ? 40 : 80,
                            height: isTablet ? 40 : 80,
                            borderRadius: "8px",
                          }}
                        />
                      </TableCell>

                      {/* Title Cell - Spans 5 columns on tablet, normal on desktop */}
                      <TableCell
                        colSpan={isTablet ? 5 : 1}
                        sx={{
                          py: isTablet ? 1 : 2,
                          maxWidth: "300px",
                          "@media (max-width: 1360px)": {
                            maxWidth: "none",
                            paddingBottom: 0,
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#333",
                            fontWeight: "500",
                            lineHeight: 1.4,
                          }}
                        >
                          {listing.title}
                        </Typography>
                      </TableCell>                      

                      {/* Desktop columns - hidden on tablet */}
                      {!isTablet && (
                        <>
                          <TableCell sx={{ py: 2 }}>
                            <Box sx={{ display: "inline-block" }}>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  fontSize: "14px",
                                  color: "#333",
                                  fontWeight: "500",
                                  lineHeight: 1.4, 
                                }}
                              >
                                {listing.subCategory === "Don't Care"
                                  ? listing.category
                                  : `${listing.category} > ${listing.subCategory}`}
                              </Typography>
                            </Box>
                          </TableCell> 

                          <TableCell sx={{ py: 2 }}>
                            <Box sx={{ display: "inline-block" }}>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  fontSize: "14px",
                                  color: "#333",
                                  fontWeight: "500",
                                  lineHeight: 1.4, 
                                }}
                              >
                                {listing.originalPrice
                                  ? listing.originalPrice.toLocaleString(undefined, {
                                    style: "currency",
                                    currency: "USD",
                                  })
                                  : "----"
                                }
                              </Typography>
                            </Box>
                          </TableCell>                          
                          <TableCell sx={{ py: 2 }}>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#333",
                              }}
                            >
                              {listing.sellingPrice
                                  ? listing.sellingPrice.toLocaleString(undefined, {
                                    style: "currency",
                                    currency: "USD",
                                  })
                                  : ""
                                }
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#333",
                              }}
                            >
                              {listing.publishedDate}
                            </Typography>
                          </TableCell>
                        </>
                      )}
                    </TableRow>

                    {/* Second row for tablet view */}
                    {isTablet && (
                      <TableRow
                        key={`${listing.id}-details`}
                        sx={{
                          "&:hover": {
                            backgroundColor: "#f9f9f9",
                          },
                          "& td": {
                            borderTop: "none",
                          },
                        }}
                      >
                        {/* Empty cell for Photo and Title column */}
                        <TableCell sx={{ py: 0 }}></TableCell>
                        <TableCell sx={{ py: 0 }}></TableCell>
                        

                        <TableCell sx={{ pt: 0, pb: 1 }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#333",
                              lineHeight: 1.4,
                            }}
                          >
                            {listing.subCategory === "Don't Care"
                            ? listing.category
                            : `${listing.category} > ${listing.subCategory}`}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ pt: 0, pb: 1 }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#333",
                              lineHeight: 1.4,
                            }}
                          >
                            {listing.originalPrice
                              ? listing.originalPrice.toLocaleString(undefined, {
                                style: "currency",
                                currency: "USD",
                              })
                              : "----"
                            }
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ pt: 0, pb: 1 }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#333",
                              lineHeight: 1.4,
                            }}
                          >
                            {listing.sellingPrice
                              ? listing.sellingPrice.toLocaleString(undefined, {
                                style: "currency",
                                currency: "USD",
                              })
                              : ""
                            }
                          </Typography>
                        </TableCell>                        
                        <TableCell sx={{ pt: 0, pb: 1 }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#333",
                              lineHeight: 1.4,
                            }}
                          >
                            {listing.publishedDate}
                          </Typography>
                        </TableCell> 
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 15, 20]}
              component="div"
              count={sortedData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: "1px solid #e0e0e0",
                "& .MuiTablePagination-toolbar": {
                  paddingLeft: 2,
                  paddingRight: 2,
                },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                  fontSize: "14px",
                  color: "#666",
                },
                "& .MuiTablePagination-select": {
                  fontSize: "14px",
                },
              }}
            />
          </TableContainer>
        </>
       )}
      </Box>

      <AlertPopup
          open={warningPopup.open}
          title={warningPopup.title}
          message=""
          confirmText="OK"
          cancelText= ""
          onConfirm={() => setWarningPopup(false)}
          onClose={() => setWarningPopup(false)}
        />
        
      <FloatingMenu left={floatMenuLeftMargin} />
    </PublicPageLayout>    
  );
};

export default PublicListings;
